import React, { useEffect, useRef, useState } from 'react';

// New complete map image with 5% included
const MAP_IMAGE = "https://customer-assets.emergentagent.com/job_repo-preview-41/artifacts/ylzdz3w3_Gazeal%20map.png";

const ScotlandComparisonSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="why-gazeal" className="relative py-12 md:py-16 bg-black overflow-hidden">
      <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="text-center mb-10 md:mb-12">
          <h2 className={`text-2xl md:text-4xl lg:text-5xl font-bold mb-4 heading transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            The <span className="text-[#59d862] relative inline-block">
              Best
              <span className="absolute -inset-1 bg-[#59d862]/20 blur-lg -z-10 animate-pulse" />
            </span> in Britain.
          </h2>
          <p className={`text-white/55 text-sm md:text-base max-w-2xl mx-auto transition-all duration-700 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            Scotland's legal system is renowned for reducing fall-throughs. <br className="hidden md:block" />
            Gazeal does it even better.
          </p>
        </div>

        {/* Keywords with glow animation */}
        <div className={`flex flex-wrap justify-center gap-4 md:gap-6 mb-10 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          {['CERTAINTY', 'SAFETY', 'GUARANTEED'].map((word, idx) => (
            <div 
              key={word}
              className="relative group cursor-default"
              style={{ animationDelay: `${idx * 200}ms` }}
            >
              <span className="text-lg md:text-2xl font-bold text-[#59d862] tracking-wider relative z-10 px-5 py-2.5 border border-[#59d862]/30 bg-[#59d862]/5 inline-block rounded-full transition-all duration-300 group-hover:bg-[#59d862]/15 group-hover:border-[#59d862]/60">
                {word}
              </span>
              <div className="absolute inset-0 bg-[#59d862] blur-xl opacity-0 group-hover:opacity-30 transition-opacity duration-300 rounded-full" />
              <div className="absolute inset-0 animate-pulse bg-[#59d862]/10 blur-md -z-10 rounded-full" style={{ animationDelay: `${idx * 300}ms` }} />
            </div>
          ))}
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left - Map Image */}
          <div className={`relative transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <div className="relative group">
              {/* Glow effect behind image */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#59d862]/20 to-[#c4b9a6]/10 blur-2xl opacity-50 group-hover:opacity-70 transition-opacity duration-500" />
              
              {/* Image container */}
              <div className="relative rounded-2xl border border-white/10 group-hover:border-[#59d862]/30 transition-colors duration-300 overflow-hidden">
                <img 
                  src={MAP_IMAGE}
                  alt="UK Fall-Through Rates - Gazeal 5% vs National Average"
                  className="w-full h-auto transform group-hover:scale-[1.02] transition-transform duration-500"
                />
              </div>
            </div>
          </div>

          {/* Right - Stats & Info */}
          <div className={`space-y-6 transition-all duration-700 delay-400 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            {/* Comparison Cards */}
            <div className="space-y-3">
              {/* Gazeal Card */}
              <div className="p-4 md:p-5 bg-[#59d862]/10 border border-[#59d862]/30 rounded-2xl relative overflow-hidden group hover:bg-[#59d862]/15 transition-colors">
                <div className="absolute top-0 right-0 w-20 h-20 bg-[#59d862] opacity-5 rounded-full blur-2xl group-hover:opacity-10 transition-opacity" />
                <div className="flex items-center justify-between">
                  <div className="text-white font-bold text-lg md:text-xl">Gazeal Process</div>
                  <div className="text-right">
                    <div className="text-3xl md:text-4xl font-bold text-[#59d862] heading">5%</div>
                    <div className="text-white/50 text-xs">Fall-through rate</div>
                  </div>
                </div>
              </div>

              {/* Scotland Card */}
              <div className="p-4 md:p-5 bg-[#c4b9a6]/10 border border-[#c4b9a6]/30 rounded-2xl relative overflow-hidden group hover:bg-[#c4b9a6]/15 transition-colors">
                <div className="absolute top-0 right-0 w-20 h-20 bg-[#c4b9a6] opacity-5 rounded-full blur-2xl group-hover:opacity-10 transition-opacity" />
                <div className="flex items-center justify-between">
                  <div className="text-white font-bold text-lg md:text-xl">Scottish System</div>
                  <div className="text-right">
                    <div className="text-3xl md:text-4xl font-bold text-[#c4b9a6] heading">12.2%</div>
                    <div className="text-white/50 text-xs">Fall-through rate</div>
                  </div>
                </div>
              </div>

              {/* England Average Card */}
              <div className="p-4 md:p-5 bg-red-500/10 border border-red-500/20 rounded-2xl relative overflow-hidden group hover:bg-red-500/15 transition-colors">
                <div className="absolute top-0 right-0 w-20 h-20 bg-red-500 opacity-5 rounded-full blur-2xl group-hover:opacity-10 transition-opacity" />
                <div className="flex items-center justify-between">
                  <div className="text-white font-bold text-lg md:text-xl">England Average</div>
                  <div className="text-right">
                    <div className="text-3xl md:text-4xl font-bold text-red-400 heading">24%</div>
                    <div className="text-white/50 text-xs">Fall-through rate</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Key Message */}
            <div className="p-4 bg-white/[0.02] border border-white/10 rounded-xl">
              <p className="text-white/70 text-sm leading-relaxed">
                The Gazeal Reservation Agreement provides <span className="text-[#59d862] font-semibold">legally binding commitment</span> that outperforms even Scotland's renowned property system. 
                This is the foundation that makes <span className="text-[#59d862] font-semibold">Gazeal Advance</span> possible.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ScotlandComparisonSection;
